import { SYSTEM_UCUM } from '@ctw/shared/api/fhir/system-urls';
import type { NormalizedUnitSystem } from '@zeus-health/types';
import type { Quantity } from 'fhir/r4';

interface ImperialConversionResult {
  /** Plain numeric total, always in `unit` regardless of which `display` form was picked. */
  value: string;
  unit: string;
  /**
   * Full string to render in place of `${value} ${unit}`, when magnitude picks a different form.
   */
  display?: string;
}

type ImperialConversion = (value: number) => ImperialConversionResult;

const CENTIMETERS_PER_INCH = 2.54;
const INCHES_PER_FOOT = 12;
const GRAMS_PER_KILOGRAM = 1000;
const POUNDS_PER_GRAM = 0.00220462;
const OUNCES_PER_GRAM = 0.035274;
const GRAMS_PER_POUND = 453.59237;

/** Rounds to `decimalPlaces`, breaking ties away from zero rather than toward +Infinity. */
function roundHalfAwayFromZero(value: number, decimalPlaces: number): number {
  const factor = 10 ** decimalPlaces;
  const scaled = value * factor;
  const rounded = scaled >= 0 ? Math.floor(scaled + 0.5) : Math.ceil(scaled - 0.5);
  return rounded / factor;
}

function convertTemperature(value: number): ImperialConversionResult {
  const fahrenheit = roundHalfAwayFromZero((value * 9) / 5 + 32, 1);
  return { value: fahrenheit.toFixed(1), unit: '°F' };
}

/**
 * Under a foot once rounded, a length renders in plain inches; at a foot or more, in feet and a
 * one-decimal inches remainder.
 */
function convertLength(value: number): ImperialConversionResult {
  const totalInches = roundHalfAwayFromZero(value / CENTIMETERS_PER_INCH, 1);
  if (totalInches < INCHES_PER_FOOT) {
    return { value: totalInches.toFixed(1), unit: 'in' };
  }

  // Whole tenths of an inch keep the feet/inches split exact, so a remainder never rounds past 12.
  const totalTenthInches = Math.round(totalInches * 10);
  const feet = Math.trunc(totalTenthInches / (INCHES_PER_FOOT * 10));
  const remainderInches = (totalTenthInches - feet * INCHES_PER_FOOT * 10) / 10;
  return {
    value: totalInches.toFixed(1),
    unit: 'in',
    display: `${feet}' ${remainderInches.toFixed(1)}"`,
  };
}

/**
 * Under a pound, a weight renders in ounces computed directly from `grams`, not from the rounded
 * pound value. At a pound or more, it renders in decimal pounds. `kg` and `g` share this
 * conversion. The pound/ounce choice compares `grams` against the exact grams-per-pound figure,
 * not the rounded pound value.
 */
function convertWeightFromGrams(grams: number): ImperialConversionResult {
  const totalPounds = roundHalfAwayFromZero(grams * POUNDS_PER_GRAM, 1);
  if (grams >= GRAMS_PER_POUND) {
    return { value: totalPounds.toFixed(1), unit: 'lb' };
  }

  const totalOunces = roundHalfAwayFromZero(grams * OUNCES_PER_GRAM, 1);
  return { value: totalPounds.toFixed(1), unit: 'lb', display: `${totalOunces.toFixed(1)} oz` };
}

/** Imperial conversions, keyed by the UCUM code of the source (metric) unit. */
const IMPERIAL_CONVERSIONS_BY_UCUM_CODE = new Map<string, ImperialConversion>([
  ['Cel', convertTemperature],
  ['kg', (value) => convertWeightFromGrams(value * GRAMS_PER_KILOGRAM)],
  ['g', convertWeightFromGrams],
  ['cm', convertLength],
]);

/** Converts a FHIR Quantity to imperial using its UCUM code, or `unit` when uncoded. */
export function convertQuantityToImperial(
  quantity: Quantity | undefined,
  unitSystem: NormalizedUnitSystem | undefined,
): ImperialConversionResult | undefined {
  if (unitSystem !== 'imperial' || !quantity || quantity.value === undefined) {
    return undefined;
  }

  const systemIsAbsent = quantity.system === undefined || quantity.system === null;
  const codeIsAbsent = quantity.code === undefined || quantity.code === null;

  let sourceCode: string | undefined;
  if (quantity.system === SYSTEM_UCUM && !codeIsAbsent) {
    sourceCode = quantity.code;
  } else if (systemIsAbsent && codeIsAbsent) {
    sourceCode = quantity.unit;
  } else {
    return undefined;
  }

  const conversion = sourceCode ? IMPERIAL_CONVERSIONS_BY_UCUM_CODE.get(sourceCode) : undefined;
  return conversion?.(quantity.value);
}
